import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { useState } from "react";
import Box from "@mui/material/Box";
import { ArchiveIcon, EditIcon, PartnerIcon2, RotateCcwIcon } from "../../shared/icons";
import {
  partnerByIdQueryOptions,
  archivePartnerMutationOptions,
  deletePartnerMutationOptions,
  unarchivePartnerMutationOptions,
} from "../../api/partners";
import type { GetPartnerByIdResponse, PartnerLifecycleStatus } from "../../api/partners";
import type { DrawerHeaderProps } from "../../shared/Drawer/Drawer";
import { Drawer } from "../../shared/Drawer/Drawer";
import { usePermission } from "../../shared/permissions";
import { PartnerEditDialog } from "./PartnerEditDialog";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../shared/ActionMenu";
import { PartnerLifecycleStatusChip } from "./PartnerLifecycleStatusChip";
import { ReplaceAndArchiveDialog } from "./ReplaceAndArchiveDialog";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { prefetch } from "../../api/prefetch";
import { useMutation } from "@tanstack/react-query";

export const PartnerDrawerHeader = ({
  partner,
  onClose,
  status,
}: {
  status: DrawerHeaderProps["status"];
  partner: GetPartnerByIdResponse;
  onClose: () => void;
}) => {
  return (
    <>
      <Drawer.Header
        title={partner.name}
        renderIcon={props => <PartnerIcon2 {...props} />}
        status={status}
        onClose={onClose}
        topRightSlot={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PartnerLifecycleStatusChip status={partner.status} />
            <PartnerActionMenu
              partnerId={partner.id}
              partnerName={partner.name}
              partnerStatus={partner.status}
              onDeleted={() => onClose()}
              ariaLabelKey="GridDrawer.Menu.ButtonLabel"
            />
          </Box>
        }
      />
    </>
  );
};

export const PartnerActionMenu = ({
  partnerId,
  partnerName,
  partnerStatus,
  onDeleted,
  ariaLabelKey,
  onHover,
}: {
  partnerId: string;
  partnerStatus: PartnerLifecycleStatus;
  partnerName: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
  onHover?: () => void;
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const deletePartnerMutation = useMutation(deletePartnerMutationOptions());
  const archivePartnerMutation = useMutation(archivePartnerMutationOptions(partnerId));
  const unarchivePartnerMutation = useMutation(unarchivePartnerMutationOptions(partnerId));
  const [isOpen, setIsOpen] = useState(false);
  type DialogState = "edit" | "archiveConfirm" | "replaceAndArchive" | null;
  const [dialogState, setDialogState] = useState<DialogState>(null);

  const closeDialog = () => {
    setDialogState(null);
    setIsOpen(false);
  };

  const openDialog = (nextDialog: DialogState) => {
    setDialogState(nextDialog);
    setIsOpen(false);
  };

  return (
    <>
      {dialogState === "edit" && (
        <PartnerEditDialog onClose={closeDialog} partnerId={partnerId} partnerName={partnerName} />
      )}
      {dialogState === "archiveConfirm" && (
        <ConfirmDeleteDialog
          isOpen
          onDelete={() => archivePartnerMutation.mutateAsync()}
          onClose={closeDialog}
          headerText={t("Partner.Archive.Dialog.Title")}
          message={
            <Translate
              translationKey="Partner.Archive.Dialog.ConfirmMessage"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {partnerName}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("Partner.Archive.Label")}
          id="confirm-archive-partner"
          onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
        />
      )}
      {dialogState === "replaceAndArchive" && <ReplaceAndArchiveDialog partnerId={partnerId} onClose={closeDialog} />}
      <ActionMenu onTriggerHover={onHover} isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
        <ActionMenuItemWithPermission
          missingPermission={!hasPermission("nova-forms-api:partner:update")}
          onClick={() => openDialog("edit")}
        >
          <EditIcon />
          {t("Partner.Edit.Label")}
        </ActionMenuItemWithPermission>

        {partnerStatus === "archived" ? (
          <ActionMenuItemWithPermission
            disabled={unarchivePartnerMutation.isPending}
            missingPermission={!hasPermission("nova-forms-api:partner:update")}
            onClick={() => {
              setIsOpen(false);
              unarchivePartnerMutation.mutate();
            }}
          >
            <RotateCcwIcon />
            {t("Partner.Archive.RestoreLabel")}
          </ActionMenuItemWithPermission>
        ) : (
          <ActionMenuItemWithPermission
            missingPermission={!hasPermission("nova-forms-api:partner:update")}
            onMouseOver={() => (partnerStatus === "active" ? prefetch(partnerByIdQueryOptions(partnerId)) : undefined)}
            onClick={() => openDialog(partnerStatus === "active" ? "replaceAndArchive" : "archiveConfirm")}
          >
            <ArchiveIcon />
            {t("Partner.Archive.Label")}
          </ActionMenuItemWithPermission>
        )}

        <ActionMenuDeleteMenuItem
          missingPermission={!hasPermission("nova-forms-api:partner:delete")}
          confirmDeleteDialog={{
            header: t("Partner.Delete.ConfirmHeader"),
            message: (
              <Translate
                translationKey="Partner.Delete.ConfirmMessage"
                values={{
                  name: (
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {partnerName}
                    </Box>
                  ),
                }}
              />
            ),
            delete: t("Partner.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsOpen(false)}
          onDelete={() => {
            return deletePartnerMutation.mutateAsync(partnerId).then(() => {
              onDeleted();
            });
          }}
        />
      </ActionMenu>
    </>
  );
};
